/**
 * Step three of government-identity verification: agency and agent registration.
 *
 * Ported from the le-portal-ui-v3 prototype's `agency-registration` step
 * (src/LoginFlow.tsx). Once the agency address is verified, a person whose agency (by
 * email domain) or own record is not on file fills in the missing details before
 * entering the portal. The verified address and the domain it implies are shown read-only:
 * they are what was just proven, so they are not editable here.
 *
 * Which sections are needed is passed in, as the server reported them, and saving is the
 * caller's: this page only collects and checks the fields. Agency fields ARS already holds
 * are shown read-only, and a complete agency on file is still shown. The agent section is
 * always shown, with any fields on file filled in and editable. The agency
 * domain is shown read-only because the server derives it from the verified address, never
 * from here.
 */
import type { FormEvent } from 'react';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Button,
  Card,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  makeStyles,
  mergeClasses,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  shorthands,
  Text,
  Title2,
  Title3,
  tokens,
  useRestoreFocusTarget,
} from '@fluentui/react-components';
import { ArrowLeftRegular, CheckmarkCircleFilled } from '@fluentui/react-icons';
import { normalizeEmail } from '../../govIdVerification';
import {
  AGENCY_FIELDS,
  agencyFieldErrors,
  agentDetailsChanged,
  agentFieldErrors,
  emailDomain,
  formatPhoneInput,
  initialAgencyDetails,
  initialAgentDetails,
  lockedAgencyFields,
  phoneFormatFor,
  toE164,
  type AgencyDetails,
  type AgencyField,
  type AgentDetails,
  type RegistrationNeeds,
} from '../../registration';
import { filterCountryOptions, isAssignedCountry, normalizeCountry } from '@/utils/countryCodes';
import { AgentAddressField } from '../AgentAddressField';
import { AGENT_ADDRESS_FIELDS } from '../../registration';
import { LOGIN_NARROW_MEDIA, useLoginStyles } from '../loginStyles';

// US numbers use the same format, placeholder and hint as the phone field in case creation
// (PhoneInput); international numbers use the +country-code form ARS recommends.
const PHONE_PLACEHOLDER = { us: '(555) 123-4567', international: '+44 20 7946 0958' } as const;
const PHONE_FORMAT_HINT = {
  us: 'Format: (XXX) XXX-XXXX',
  international: 'Include the country code, starting with +.',
} as const;

// Header and footer bands span the page but keep their content on the 760px column.
const bandInline = `max(${tokens.spacingHorizontalXXL}, calc((100% - 760px) / 2))`;

const useStyles = makeStyles({
  page: {
    display: 'grid',
    // Header and footer size to content; the form takes the rest, keeping the footer at
    // the bottom on short forms instead of the rows sharing the height.
    gridTemplateRows: 'auto 1fr auto',
    width: '100%',
    minHeight: '100%',
  },

  header: {
    ...shorthands.padding(tokens.spacingVerticalXL, bandInline),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    '& h1': {
      ...shorthands.margin(0, 0, tokens.spacingVerticalL),
    },
    '& p': {
      maxWidth: '72ch',
      ...shorthands.margin(0),
      color: tokens.colorNeutralForeground3,
      lineHeight: 1.5,
    },
    [LOGIN_NARROW_MEDIA]: {
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
    },
  },

  status: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginBottom: tokens.spacingVerticalS,
    color: tokens.colorPaletteGreenForeground1,
    '& svg': {
      fontSize: '20px',
    },
  },

  content: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    // The page row gives this the leftover height; keep the cards at their own height
    // rather than stretching a lone section to fill it.
    alignContent: 'start',
    ...shorthands.gap(tokens.spacingVerticalXL),
    width: 'min(760px, calc(100% - 48px))',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    [LOGIN_NARROW_MEDIA]: {
      width: 'calc(100% - 32px)',
    },
  },

  cards: {
    display: 'grid',
    ...shorthands.gap(tokens.spacingVerticalXL),
    width: '100%',
    minWidth: 0,
  },

  sectionCard: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    alignSelf: 'stretch',
    ...shorthands.padding(tokens.spacingVerticalXXL),
    backgroundColor: tokens.colorNeutralBackground1,
  },

  section: {
    display: 'grid',
    ...shorthands.gap(tokens.spacingVerticalL),
    minWidth: 0,
  },

  sectionHeading: {
    display: 'grid',
    ...shorthands.gap(tokens.spacingVerticalXS),
    minWidth: 0,
    '& h2': {
      ...shorthands.margin(0),
    },
    '& > span': {
      ...shorthands.margin(0),
      color: tokens.colorNeutralForeground3,
      lineHeight: 1.5,
    },
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    // A hint or error under one field makes its row taller; without this its neighbour's
    // input stretches to match.
    alignItems: 'start',
    ...shorthands.gap(tokens.spacingVerticalXL, tokens.spacingHorizontalL),
    width: '100%',
    minWidth: 0,
    '& > *': {
      minWidth: 0,
    },
    '& input': {
      width: '100%',
    },
    '& textarea': {
      width: '100%',
      minHeight: '72px',
    },
    [LOGIN_NARROW_MEDIA]: {
      gridTemplateColumns: '1fr',
    },
  },

  // Read-only rather than disabled, so the values stay focusable, readable and copyable;
  // the muted fill is what says "set for you, not editable here".
  locked: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
    '& input': {
      color: tokens.colorNeutralForeground2,
    },
  },

  // Fluent's Combobox has a 250px minimum; let it fill its grid cell like the inputs do.
  combobox: {
    width: '100%',
    minWidth: 0,
  },

  countryListbox: {
    maxHeight: '320px',
  },

  fullWidth: {
    gridColumn: '1 / -1',
    [LOGIN_NARROW_MEDIA]: {
      gridColumn: 'auto',
    },
  },

  // Layered on the shared footer row: it stays in view while the form scrolls under it.
  actions: {
    position: 'sticky',
    bottom: 0,
    zIndex: 5,
    ...shorthands.padding(tokens.spacingVerticalM, bandInline),
    backgroundColor: `color-mix(in srgb, ${tokens.colorNeutralBackground1} 96%, transparent)`,
    backdropFilter: 'blur(12px)',
    [LOGIN_NARROW_MEDIA]: {
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
    },
  },
});

export interface RegistrationPageProps {
  /** The verified official agency address. */
  email: string;

  /** The signed-in Microsoft account, shown for reference. */
  microsoftAccount: string;

  /** Which sections to ask for. */
  needs: RegistrationNeeds;

  /** Called with the entered details once every required field is filled. */
  onComplete: (details: { agency?: AgencyDetails; agent?: AgentDetails }) => void;

  /** Whether the entered details are being saved; Continue is held while they are. */
  isSaving?: boolean;

  /** Why the last save failed, shown above the form, or null when it has not. */
  saveError?: string | null;

  /** Returns to the previous step. */
  onBack: () => void;
}

export function RegistrationPage({
  email,
  microsoftAccount,
  needs,
  onComplete,
  isSaving = false,
  saveError = null,
  onBack,
}: RegistrationPageProps) {
  const loginStyles = useLoginStyles();
  const styles = useStyles();
  const [agency, setAgency] = useState<AgencyDetails>(() => initialAgencyDetails(needs.agencyOnFile));
  const [agent, setAgent] = useState<AgentDetails>(() =>
    initialAgentDetails(needs.agentOnFile, needs.agencyOnFile?.country),
  );
  // Agency fields ARS already holds are shown greyed out and cannot be changed here. Agent
  // fields on file are only pre-filled: the agent can correct their own details. A complete
  // agency is not updated here, so its blank optional fields are locked too.
  const [lockedAgency] = useState<readonly AgencyField[]>(() =>
    needs.agency ? lockedAgencyFields(needs.agencyOnFile) : [...AGENCY_FIELDS],
  );
  // Errors appear only after a submit attempt, then track the fields live, so nobody is
  // shown a wall of red before they have typed anything.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [confirmingBack, setConfirmingBack] = useState(false);
  // Back opens the dialog without being its DialogTrigger, so mark it as where focus
  // returns when the dialog is dismissed.
  const restoreFocusTarget = useRestoreFocusTarget();

  const normalizedEmail = normalizeEmail(email);
  const domain = emailDomain(email);
  const agencyErrors = needs.agency ? agencyFieldErrors(agency, lockedAgency) : {};
  const agentErrors = agentFieldErrors(agent, agency.country);
  const countryOptions = filterCountryOptions(agency.country);
  const agencyPhoneFormat = phoneFormatFor(agency.phone, agency.country);
  const agentPhoneFormat = phoneFormatFor(agent.phone, agency.country);

  const updateAgency = (field: keyof AgencyDetails) => (value: string) =>
    setAgency((current) => ({ ...current, [field]: value }));
  const updateAgent = (field: keyof AgentDetails) => (value: string) =>
    setAgent((current) => ({ ...current, [field]: value }));

  const errorProps = (message: string | undefined) =>
    submitAttempted && message !== undefined
      ? { validationState: 'error' as const, validationMessage: message }
      : {};
  const agencyError = (field: keyof AgencyDetails) => errorProps(agencyErrors[field]);
  const agentError = (field: keyof AgentDetails) => errorProps(agentErrors[field]);
  const agencyInput = (field: AgencyField) =>
    lockedAgency.includes(field)
      ? { value: agency[field], readOnly: true, className: styles.locked }
      : { value: agency[field], onChange: (_: unknown, data: { value: string }) => updateAgency(field)(data.value) };
  const agentInput = (field: keyof AgentDetails) => ({
    value: agent[field],
    onChange: (_: unknown, data: { value: string }) => updateAgent(field)(data.value),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSaving) {
      return;
    }

    if (Object.keys(agencyErrors).length > 0 || Object.keys(agentErrors).length > 0) {
      // Render the errors before moving focus, so a screen reader announces the field
      // as invalid with its message rather than as a plain required field.
      flushSync(() => setSubmitAttempted(true));
      event.currentTarget.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      return;
    }

    // Phones are saved in E.164; a locked agency phone is already on file, so it is not sent.
    onComplete({
      agency: needs.agency
        ? {
            ...agency,
            country: normalizeCountry(agency.country),
            phone: lockedAgency.includes('phone') ? '' : (toE164(agency.phone, agency.country) ?? ''),
          }
        : undefined,
      agent: agentDetailsChanged(agent, needs.agentOnFile, agency.country)
        ? { ...agent, country: normalizeCountry(agent.country), phone: toE164(agent.phone, agency.country) ?? '' }
        : undefined,
    });
  };

  return (
    <form className={styles.page} onSubmit={handleSubmit} noValidate>
      <div className={styles.header}>
        <div className={styles.status}>
          <CheckmarkCircleFilled aria-hidden="true" />
          <Text weight="semibold">Sign-in complete</Text>
        </div>
        <Title2 as="h1" id="login-content-title" tabIndex={-1}>
          Before you enter the portal
        </Title2>
        <p>
          {needs.agency ? (
            <>
              Your official agency email is verified. We could not find complete records for{' '}
              <strong>{domain}</strong>, so we need a few details before you can enter the Law
              Enforcement Portal.
            </>
          ) : (
            <>
              Your official agency email is verified, and we found the agency associated with{' '}
              <strong>{domain}</strong>. We just need a few details about you before you can
              enter the Law Enforcement Portal.
            </>
          )}
        </p>
      </div>

      <div className={styles.content}>
        {saveError !== null && (
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>Your details were not saved</MessageBarTitle>
              {saveError}
            </MessageBarBody>
          </MessageBar>
        )}
        <MessageBar intent="info" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>Why we need these details</MessageBarTitle>
            Your verified email determines the agency domain, so the domain and official agency
            email cannot be changed here.
          </MessageBarBody>
        </MessageBar>

        <div className={styles.cards}>
          {/* A complete agency on file is still shown, every field locked, so the agent
              can see which agency they are joining. */}
          {(needs.agency || needs.agencyOnFile !== undefined) && (
            <Card appearance="outline" className={styles.sectionCard}>
              <section
                className={styles.section}
                aria-labelledby="agency-details-title"
              >
                <div className={styles.sectionHeading}>
                  <Title3 as="h2" id="agency-details-title">
                    Agency details
                  </Title3>
                  <Text>
                    {needs.agency
                      ? 'Enter information for the law enforcement agency associated with your official email.'
                      : 'The law enforcement agency on file for your official email.'}
                  </Text>
                </div>

                <div className={styles.grid}>
                  <Field label="Agency email domain" hint="Set from your verified email.">
                    <Input className={styles.locked} value={domain} readOnly />
                  </Field>
                  <Field label="Agency full name" required {...agencyError('name')}>
                    <Input autoComplete="organization" {...agencyInput('name')} />
                  </Field>
                  <Field
                    className={styles.fullWidth}
                    label="Street address"
                    required
                    {...agencyError('street')}
                  >
                    <Input autoComplete="street-address" {...agencyInput('street')} />
                  </Field>
                  <Field label="City" required {...agencyError('city')}>
                    <Input autoComplete="address-level2" {...agencyInput('city')} />
                  </Field>
                  <Field label="State or province" {...agencyError('state')}>
                    <Input autoComplete="address-level1" {...agencyInput('state')} />
                  </Field>
                  <Field label="ZIP or postal code" {...agencyError('zip')}>
                    <Input autoComplete="postal-code" {...agencyInput('zip')} />
                  </Field>
                  <Field
                    label="Country"
                    hint={
                      lockedAgency.includes('country')
                        ? undefined
                        : 'Two-letter ISO code. Type a code or name, then pick from the list.'
                    }
                    required
                    {...agencyError('country')}
                  >
                    {lockedAgency.includes('country') ? (
                      <Input {...agencyInput('country')} />
                    ) : (
                      <Combobox
                        className={styles.combobox}
                        listbox={{ className: styles.countryListbox }}
                        freeform
                        autoComplete="country"
                        value={agency.country}
                        selectedOptions={
                          isAssignedCountry(agency.country) ? [normalizeCountry(agency.country)] : []
                        }
                        onChange={(event) => updateAgency('country')(event.target.value)}
                        onOptionSelect={(_, data) => {
                          if (data.optionValue) {
                            updateAgency('country')(data.optionValue);
                          }
                        }}
                        // Settle a typed "us" to the canonical "US" once the user moves on.
                        onBlur={() => {
                          if (isAssignedCountry(agency.country)) {
                            updateAgency('country')(normalizeCountry(agency.country));
                          }
                        }}
                      >
                        {countryOptions.length > 0 ? (
                          countryOptions.map(({ code, name }) => (
                            <Option key={code} value={code} text={code}>
                              {`${code} – ${name}`}
                            </Option>
                          ))
                        ) : (
                          <Option disabled value="" text="">
                            No matching country
                          </Option>
                        )}
                      </Combobox>
                    )}
                  </Field>
                  <Field
                    label="Agency phone number"
                    required
                    hint={lockedAgency.includes('phone') ? undefined : PHONE_FORMAT_HINT[agencyPhoneFormat]}
                    {...agencyError('phone')}
                  >
                    {/* No autoComplete: "tel" would offer the user's own number, which is
                        not the agency's. */}
                    <Input
                      type="tel"
                      placeholder={PHONE_PLACEHOLDER[agencyPhoneFormat]}
                      {...agencyInput('phone')}
                      {...(lockedAgency.includes('phone')
                        ? {}
                        : {
                            onChange: (_: unknown, data: { value: string }) =>
                              updateAgency('phone')(formatPhoneInput(data.value, agency.country)),
                          })}
                    />
                  </Field>
                  <Field
                    label="Jurisdiction or geographic area"
                    required
                    {...agencyError('jurisdiction')}
                  >
                    <Input id="agency-jurisdiction" {...agencyInput('jurisdiction')} />
                  </Field>
                </div>
              </section>
            </Card>
          )}

          {/* Always shown, so the agent can review and correct their own details. */}
          <Card appearance="outline" className={styles.sectionCard}>
            <section
              className={styles.section}
              aria-labelledby="agent-details-title"
            >
              <div className={styles.sectionHeading}>
                <Title3 as="h2" id="agent-details-title">
                  Your details
                </Title3>
                <Text>Tell us who you are and how your agency can contact you.</Text>
              </div>

              <div className={styles.grid}>
                <Field label="Official agency email">
                  <Input className={styles.locked} value={normalizedEmail} readOnly />
                </Field>
                <Field label="MSA email ID">
                  <Input className={styles.locked} value={microsoftAccount} readOnly />
                </Field>
                <Field label="Full name" required {...agentError('displayName')}>
                  <Input autoComplete="name" {...agentInput('displayName')} />
                </Field>
                <Field label="Title or rank" required {...agentError('titleRank')}>
                  <Input {...agentInput('titleRank')} />
                </Field>
                <Field label="Role" required {...agentError('role')}>
                  <Input {...agentInput('role')} />
                </Field>
                <Field
                  label="Phone number"
                  required
                  hint={PHONE_FORMAT_HINT[agentPhoneFormat]}
                  {...agentError('phone')}
                >
                  <Input
                    type="tel"
                    autoComplete="tel"
                    placeholder={PHONE_PLACEHOLDER[agentPhoneFormat]}
                    value={agent.phone}
                    onChange={(_, data) => updateAgent('phone')(formatPhoneInput(data.value, agency.country))}
                  />
                </Field>
                {AGENT_ADDRESS_FIELDS.map((field) => (
                  <AgentAddressField
                    key={field}
                    field={field}
                    value={agent[field]}
                    onChange={updateAgent(field)}
                    error={submitAttempted ? agentErrors[field] : undefined}
                    readOnly={isSaving}
                  />
                ))}
              </div>
            </section>
          </Card>
        </div>
      </div>

      <div className={mergeClasses(loginStyles.footerActions, styles.actions)}>
        <Button
          {...restoreFocusTarget}
          appearance="secondary"
          type="button"
          icon={<ArrowLeftRegular />}
          onClick={() => setConfirmingBack(true)}
          disabled={isSaving}
        >
          Back
        </Button>
        <Button appearance="primary" type="submit" disabledFocusable={isSaving}>
          {isSaving ? 'Saving…' : 'Continue'}
        </Button>
      </div>

      {/* Going back cannot return to the code screen (verifying used the code up), so it
          restarts at the address step and loses what was typed here. Confirm first. */}
      <Dialog
        open={confirmingBack}
        onOpenChange={(_, data) => setConfirmingBack(data.open)}
        modalType="alert"
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Start over?</DialogTitle>
            <DialogContent>
              Going back clears the details you entered here, and you will need a new code
              sent to your agency email.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmingBack(false)}>
                Stay here
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  setConfirmingBack(false);
                  onBack();
                }}
              >
                Start over
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </form>
  );
}
